import type { SearchFocusTarget, User } from "@/app/types";
import { useApp } from "@/contexts/AppContext";
import { Header } from "./Header";
import { Footer } from "./Footer";
import { Button } from "./ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "./ui/card";
import { AlertCircle, Home, Search, ArrowLeft } from "lucide-react";
import { useEffect, type ReactNode } from "react";

export type ErrorVariant = "404" | "403" | "500" | "maintenance";

interface ErrorPageProps {
  navigate: (page: string) => void;
  currentUser: User | null;
  onLogout: () => void;
  onNavigateToSearchPage: (target: SearchFocusTarget) => void;
  variant: ErrorVariant;
  details?: string;
  extraActions?: ReactNode;
}

const getVariantContent = (
  variant: ErrorVariant,
  t: (key: string) => string
) => {
  switch (variant) {
    case "403":
      return {
        title: t("errorPage.title403"),
        subtitle: t("errorPage.subtitle403"),
        description: t("errorPage.whatHappenedDesc403"),
        reasons: [
          t("errorPage.reasonPermission"),
          t("errorPage.reason403Login"),
        ],
      };
    case "500":
      return {
        title: t("errorPage.title500"),
        subtitle: t("errorPage.subtitle500"),
        description: t("errorPage.whatHappenedDesc500"),
        reasons: [
          t("errorPage.reason500Server"),
          t("errorPage.reason500Retry"),
        ],
      };
    case "maintenance":
      return {
        title: t("errorPage.titleMaintenance"),
        subtitle: t("errorPage.subtitleMaintenance"),
        description: t("errorPage.whatHappenedDescMaintenance"),
        reasons: [
          t("errorPage.reasonMaintenanceScheduled"),
          t("errorPage.reasonMaintenanceUpdates"),
        ],
      };
    case "404":
    default:
      return {
        title: t("errorPage.title404"),
        subtitle: t("errorPage.subtitle404"),
        description: t("errorPage.whatHappenedDesc"),
        reasons: [
          t("errorPage.reasonWrongUrl"),
          t("errorPage.reasonMoved"),
          t("errorPage.reasonOutdated"),
          t("errorPage.reasonPermission"),
        ],
      };
  }
};

export function ErrorPage({
  navigate,
  currentUser,
  onLogout,
  onNavigateToSearchPage,
  variant,
  details,
  extraActions,
}: ErrorPageProps) {
  const { t } = useApp();
  const iconStyle = {
    color:
      "color-mix(in srgb, color-mix(in srgb, var(--primary) 70%, var(--accent)) 90%, var(--foreground))",
  } as const;
  const primaryTextStyle = {
    color: "var(--foreground)",
  } as const;
  const secondaryTextStyle = {
    color: "color-mix(in srgb, var(--foreground) 82%, transparent)",
  } as const;
  const mutedTextStyle = {
    color: "color-mix(in srgb, var(--foreground) 70%, transparent)",
  } as const;
  const iconBadgeStyle = {
    backgroundColor:
      "color-mix(in srgb, var(--surface) 70%, var(--background))",
  } as const;
  const helpPanelStyle = {
    backgroundColor:
      "color-mix(in srgb, var(--primary) 12%, var(--background))",
    borderColor:
      "color-mix(in srgb, var(--primary) 35%, var(--background))",
  } as const;
  const separatorStyle = {
    color: "color-mix(in srgb, var(--foreground) 40%, transparent)",
  } as const;
  const { title, subtitle, description, reasons } = getVariantContent(
    variant,
    t
  );

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div
      className="flex flex-col min-h-screen"
      style={{ backgroundColor: "var(--background)" }}
    >
      <Header
        navigate={navigate}
        currentUser={currentUser}
        onLogout={onLogout}
        onNavigateToSearchPage={onNavigateToSearchPage}
      />

      <main className="flex-1 flex items-center justify-center py-16 px-4">
        <div className="max-w-2xl w-full text-center">
          {/* Error Icon */}
          <div className="mb-8">
            <div
              className="inline-flex items-center justify-center w-24 h-24 rounded-full mb-4"
              style={iconBadgeStyle}
            >
              <AlertCircle className="w-12 h-12" style={iconStyle} />
            </div>
            <h1 className="text-primary-heading mb-2">{title}</h1>
            <p className="text-lg" style={secondaryTextStyle}>
              {subtitle}
            </p>
          </div>

          {/* Error Card */}
          <Card className="mb-8 stat-card">
            <CardHeader>
              <CardTitle className="card-title-text" style={{ color: "var(--heading-color)" }}>
                {t("errorPage.whatHappenedTitle")}
              </CardTitle>
              <CardDescription className="card-description-text" style={secondaryTextStyle}>
                {description}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4 text-left">
                <p className="text-body" style={primaryTextStyle}>
                  {t("errorPage.reasonsIntro")}
                </p>
                <ul
                  className="list-disc list-inside space-y-2"
                  style={secondaryTextStyle}
                >
                  {reasons.map((reason) => (
                    <li key={reason}>{reason}</li>
                  ))}
                </ul>
                {details && (
                  <p className="text-xs whitespace-pre-line" style={mutedTextStyle}>
                    {details}
                  </p>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              onClick={() => navigate("home")}
              size="lg"
              className="btn-primary gap-2"
            >
              <Home className="w-5 h-5" />
              {t("errorPage.goHome")}
            </Button>
            <Button
              onClick={() => navigate("search")}
              size="lg"
              variant="outline"
              className="gap-2"
            >
              <Search className="w-5 h-5" />
              {t("errorPage.startSearch")}
            </Button>
            <Button
              onClick={() => window.history.back()}
              size="lg"
              variant="outline"
              className="gap-2"
            >
              <ArrowLeft className="w-5 h-5" />
              {t("errorPage.goBack")}
            </Button>
          </div>

          {extraActions && <div className="mt-8">{extraActions}</div>}

          {/* Help Section */}
          <div className="mt-12 p-6 rounded-lg border" style={helpPanelStyle}>
            <h3 className="card-title-text mb-3" style={primaryTextStyle}>
              {t("errorPage.needHelpTitle")}
            </h3>
            <p className="text-body mb-4" style={primaryTextStyle}>
              {t("errorPage.needHelpText")}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                variant="outline"
                className="gap-2"
                onClick={() => navigate("hilfe")}
              >
                {t("errorPage.visitHelpCenter")}
              </Button>
              <Button
                variant="outline"
                className="gap-2"
                onClick={() => navigate("impressum")}
              >
                {t("errorPage.contactSupport")}
              </Button>
            </div>
          </div>

          {/* Quick Links */}
          <div className="mt-8">
            <p className="text-sm mb-4" style={secondaryTextStyle}>
              {t("errorPage.quickLinksIntro")}
            </p>
            <div className="flex flex-wrap gap-2 justify-center">
              <Button
                variant="link"
                onClick={() => navigate("categories")}
                style={iconStyle}
              >
                {t("errorPage.categories")}
              </Button>
              <span style={separatorStyle}>|</span>
              <Button
                variant="link"
                onClick={() => navigate("statistics")}
                style={iconStyle}
              >
                {t("errorPage.statistics")}
              </Button>
              {currentUser ? (
                <>
                  <span style={separatorStyle}>|</span>
                  <Button
                    variant="link"
                    onClick={() => navigate("account")}
                    style={iconStyle}
                  >
                    {t("errorPage.myAccount")}
                  </Button>
                  {currentUser.role === "admin" && (
                    <>
                      <span style={separatorStyle}>|</span>
                      <Button
                        variant="link"
                        onClick={() => navigate("admin")}
                        style={iconStyle}
                      >
                        {t("errorPage.administration")}
                      </Button>
                    </>
                  )}
                </>
              ) : (
                <>
                  <span style={separatorStyle}>|</span>
                  <Button
                    variant="link"
                    onClick={() => navigate("login")}
                    style={iconStyle}
                  >
                    {t("errorPage.login")}
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      </main>

      <Footer navigate={navigate} />
    </div>
  );
}
